import { Button, CopyButton } from '@mantine/core';
import { useDisclosure, useMediaQuery } from '@mantine/hooks';
import {
	type MotionValue,
	motion,
	useMotionValueEvent,
	useTransform
} from 'framer-motion';
import parsePhoneNumber from 'libphonenumber-js';
import { Check, Copy, Phone } from 'lucide-react';
import { type FC, useMemo } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useHostContext } from '@/context/HostContext.tsx';
import useWebRtcCall from '@/hooks/useWebRtcCall.ts';
import styles from '@/pages/AgentSuccess/AgentSuccess.module.css';
import CallDialog from '@/pages/AgentSuccess/components/AgentSuccessHeader/components/CallDialog';
import { useAgentSuccessActions } from '@/pages/AgentSuccess/components/StepsBlock/hooks/useAgentSuccessActions.tsx';
import { partnerMetadata } from '@/services/PartnerMetadata';
import useAppStore from '@/store/app.ts';
import { cn } from '@/utils';

enum LayoutMode {
	Mini = 'mini',
	Mobile = 'mobile',
	Desktop = 'desktop'
}

type AgentSuccessHeaderProps = {
	progress: MotionValue<number>;
};

const toPx = (v: number) => `${v}px`;

const AgentSuccessHeader: FC<AgentSuccessHeaderProps> = ({ progress }) => {
	const { localization } = useHostContext();
	const isMobile = useMediaQuery('(max-width: 768px)');
	const [isCompact, { close: disableCompactMode, open: enableCompactMode }] =
		useDisclosure(false);
	const [isCallDialogOpened, { close: callDialogClose, open: openCallDialog }] =
		useDisclosure(false);
	const agentData = useAppStore((s) => s.agentData);

	const { isMiniCreatorMode } = useHostContext();
	const { startAccountActivation } = useAgentSuccessActions();

	const isEnabledWebRtcWidget = !!globalThis?.WebCallWidget;

	const parsed = useMemo(() => {
		if (!agentData?.phone_number) {
			return null;
		}
		return parsePhoneNumber(`+${agentData.phone_number}`);
	}, [agentData?.phone_number]);

	const international = parsed?.formatInternational() ?? '';
	const uri = parsed?.getURI() ?? '';

	const layoutMode: LayoutMode = useMemo(() => {
		if (isMiniCreatorMode) {
			return LayoutMode.Mini;
		}

		if (isMobile) {
			return LayoutMode.Mobile;
		}

		return LayoutMode.Desktop;
	}, [isMiniCreatorMode, isMobile]);

	const titlePreset = useMemo(() => {
		const config = {
			[LayoutMode.Mini]: { gap: [18, 18], font: [18, 18] },
			[LayoutMode.Mobile]: { gap: [29, 27], font: [18, 16] },
			[LayoutMode.Desktop]: { gap: [29, 27], font: [32, 16] }
		};

		return config[layoutMode];
	}, [layoutMode]);

	const buttonPreset = useMemo(() => {
		const isTooLong = international?.length > 15;
		if (partnerMetadata.isSeparateActivateButtonEnabled()) {
			return {
				padding: [20, 10],
				height: [60, 30],
				font: isTooLong ? [16, 14] : [18, 16]
			};
		}

		const config = {
			[LayoutMode.Mini]: {
				padding: [20, 20],
				height: [40, 40],
				font: isTooLong ? [14, 14] : [18, 18]
			},
			[LayoutMode.Mobile]: {
				padding: [20, 10],
				height: [68, 32],
				font: isTooLong ? [16, 14] : [20, 18]
			},
			[LayoutMode.Desktop]: {
				padding: [60, 40],
				height: [128, 68],
				font: isTooLong ? [56, 42] : [64, 46]
			}
		};

		return config[layoutMode];
	}, [layoutMode, international]);

	const titleScale = useTransform(
		progress,
		[0, 1],
		isMiniCreatorMode ? [1, 1] : [1, 0.78]
	);

	const titleGap = useTransform(progress, (v) => {
		const a = titlePreset.gap[0];
		const b = titlePreset.gap[1];
		return toPx(a + (b - a) * v);
	});

	const titleFontSize = useTransform(progress, (v) => {
		const a = titlePreset.font[0];
		const b = titlePreset.font[1];
		return toPx(a + (b - a) * v);
	});

	const btnPaddingX = useTransform(progress, (v) => {
		const a = buttonPreset.padding[0];
		const b = buttonPreset.padding[1];
		return toPx(a + (b - a) * v);
	});

	const btnHeight = useTransform(progress, (v) => {
		const a = buttonPreset.height[0];
		const b = buttonPreset.height[1];
		return toPx(a + (b - a) * v);
	});

	const btnFontSize = useTransform(progress, (v) => {
		const a = buttonPreset.font[0];
		const b = buttonPreset.font[1];
		return toPx(a + (b - a) * v);
	});

	const handleOpenWebCall = useWebRtcCall({
		showButton: false,
		useLogger: false,
		customerIdn: agentData?.created_customer_idn,
		connectorIdn: agentData?.webrtc_connector_idn,
		externalActorId: uuidv4()
	});

	useMotionValueEvent(progress, 'change', (v) => {
		if (v > 0.2 && !isMiniCreatorMode) {
			enableCompactMode();
		} else {
			disableCompactMode();
		}
	});

	const iconColor = partnerMetadata.isSeparateActivateButtonEnabled()
		? 'black'
		: 'white';

	return (
		<div
			className={cn('w-full mb-[34px] shrink-0', {
				'mb-0':
					isMiniCreatorMode || partnerMetadata.isSeparateActivateButtonEnabled()
			})}
		>
			<div
				className={cn(
					'bg-[#EFF6FF] rounded-[30px] flex flex-col items-center px-[23px] py-[32px]',
					{ 'rounded-none p-2': isMiniCreatorMode }
				)}
			>
				<motion.div
					className="flex flex-col items-center"
					style={{ scale: titleScale, gap: titleGap }}
				>
					<motion.h5
						className="text-center [font-family:var(--font-headlines)] font-bold m-0 leading-9"
						data-testid="agent-success-heading"
						style={{ fontSize: titleFontSize }}
					>
						<span>
							{localization.rich('creator', 'agent_success.title', {
								br: () => <br />
							})}
						</span>
					</motion.h5>

					<motion.div
						style={
							{
								'--btn-px': btnPaddingX,
								'--btn-h': btnHeight,
								'--btn-fs': btnFontSize
							} as React.CSSProperties
						}
					>
						<div
							className={cn('justify-center w-full', {
								'grid grid-cols-[1fr] md:grid-cols-[1fr_1fr] items-center gap-[10px]  max-w-[550px]':
									partnerMetadata.isSeparateActivateButtonEnabled()
							})}
						>
							{partnerMetadata.isSeparateActivateButtonEnabled() && (
								<Button
									className={cn(
										'group cursor-pointer border hover:border-transparent flex items-center justify-center !rounded-[20px] gap-[10px]',
										{
											'!text-black !bg-white !border-primary-900': isCompact,
											'text-lg': isMiniCreatorMode
										}
									)}
									onClick={() => {
										startAccountActivation();
									}}
									styles={{
										root: {
											height: 'var(--btn-h)',
											paddingLeft: 'var(--btn-px)',
											paddingRight: 'var(--btn-px)'
										},
										label: {
											overflow: 'initial',
											fontSize: 'var(--btn-fs)',
											lineHeight: 'var(--btn-fs)'
										}
									}}
								>
									{localization.translate(
										'creator',
										'agent_success.next_steps.activate_account.button_label'
									)}
								</Button>
							)}

							<Button
								className={cn(
									'max-w-[667px] w-full group bg-primary-900 text-white cursor-pointer !rounded-[20px] border hover:border-transparent flex items-center justify-center gap-[10px]',
									{
										'!text-black !bg-white !border-primary-900': isCompact,
										'!border-primary-900 !text-primary-900 !bg-white':
											partnerMetadata.isSeparateActivateButtonEnabled(),
										'text-lg': isMiniCreatorMode,
										[styles.heartBeat]: !isCompact,
										'duration-200':
											!partnerMetadata.isSeparateActivateButtonEnabled()
									}
								)}
								leftSection={
									<div
										className={cn('flex px-3 items-center', {
											'!px-2':
												partnerMetadata.isSeparateActivateButtonEnabled(),
											'px-3': isMobile
										})}
									>
										<Phone
											className="!text-xl"
											color={iconColor}
										/>
									</div>
								}
								onClick={(ev) => {
									if (isEnabledWebRtcWidget) {
										if (partnerMetadata.isCallViaDeviceHidden()) {
											handleOpenWebCall(ev);
											return;
										}
										openCallDialog();
										return;
									}
									window.location.assign(uri);
								}}
								rightSection={
									<CopyButton value={international}>
										{({ copy, copied }) => (
											<button
												className={cn(
													'px-3 flex content-center justify-center border-0 bg-primary-900',
													{
														'px-2':
															partnerMetadata.isSeparateActivateButtonEnabled(),
														'px-3': isMobile
													}
												)}
												onClick={(ev) => {
													ev.preventDefault();
													ev.stopPropagation();
													copy();
												}}
												type="button"
											>
												{copied ? (
													<Check
														className="!text-xl"
														color={iconColor}
													/>
												) : (
													<Copy
														className="!text-xl"
														color={iconColor}
													/>
												)}
											</button>
										)}
									</CopyButton>
								}
								styles={{
									root: {
										height: 'var(--btn-h)',
										paddingLeft: 'var(--btn-px)',
										paddingRight: 'var(--btn-px)'
									},
									label: {
										overflow: 'initial',
										fontSize: 'var(--btn-fs)',
										lineHeight: 'var(--btn-fs)'
									}
								}}
							>
								{international}
							</Button>
						</div>
					</motion.div>
				</motion.div>

				<div
					className={cn('grid grid-cols-3 w-full max-w-[639px] mt-[43px]', {
						'mt-[27px]': isCompact
					})}
				>
					<div className="flex flex-col !gap-[12px] items-center">
						<span className="text-[12.5px] opacity-50">
							{localization.translate('creator', 'agent_success.agent_name')}
						</span>
						<p
							className={cn(
								'text-[18px] !m-0 px-[8px] truncate max-w-full overflow-hidden text-ellipsis whitespace-nowrap',
								{
									'sm:text-[23px]': !isMiniCreatorMode,
									'text-base': isCompact || isMiniCreatorMode
								}
							)}
						>
							{agentData?.agent_name}
						</p>
					</div>

					<div className="flex flex-col gap-[12px] items-center border-l border-r border-gray-300">
						<span className="text-[12.5px] opacity-50">
							{localization.translate('creator', 'agent_success.position')}
						</span>
						<p
							className={cn(
								'text-[18px] !m-0 px-[8px] truncate max-w-full overflow-hidden text-ellipsis whitespace-nowrap',
								{
									'sm:text-[23px]': !isMiniCreatorMode,
									'text-base': isCompact || isMiniCreatorMode
								}
							)}
						>
							{agentData?.agent_title}
						</p>
					</div>

					<div className="flex flex-col gap-[12px] items-center">
						<span className="text-[12.5px] opacity-50">
							{localization.translate('creator', 'agent_success.organization')}
						</span>
						<p
							className={cn(
								'text-[18px] !m-0 px-[8px] truncate max-w-full overflow-hidden text-ellipsis whitespace-nowrap',
								{
									'sm:text-[23px]': !isMiniCreatorMode,
									'text-base': isCompact || isMiniCreatorMode
								}
							)}
						>
							{agentData?.business_name}
						</p>
					</div>
				</div>
			</div>
			<CallDialog
				callDialogClose={callDialogClose}
				handleOpenWebCall={handleOpenWebCall}
				isCallDialogOpened={isCallDialogOpened}
				uri={uri}
			/>
		</div>
	);
};

export default AgentSuccessHeader;
